import React, { useCallback } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import isEmpty from 'lodash/isEmpty';

import useHotkeys from '@lyearn/core/hooks/useHotkeys';
import Platform from '@lyearn/core/utils/Platform';
import { cn } from '@lyearn/style';
import { BaseButton, Box, Stack } from '@lyearn/ui';
import { OPTION_ITEM_CONFIG } from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/consts';
import {
  renderKeyCode,
  renderOptionText,
} from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/renderers';

import { getElementListItemType } from '../../../../../utils';
import { renderArrow } from '../../renderers';
import { LeftElementItemProps } from './types';

const isNative = Platform.OS !== 'web';

const LeftElementItem: React.FC<LeftElementItemProps> = (props) => {
  const {
    element,
    containerEl,
    index,
    selectedElementId,
    isQuestionSubmitted,
    mtfResponse,
    selectedElementLayouts,
    setSelectedElementLayouts,
    correctMtfPairings,
    setSelectedElementId,
    onChange,
  } = props;

  const [layout, setLayout] = React.useState<LayoutChangeEvent['nativeEvent']['layout']>();

  const { type: leftElementType } = getElementListItemType({
    element: element,
    selectedElementId,
    isQuestionSubmitted,
    mtfResponse,
    correctMtfPairings,
    side: 'left',
  });
  const leftElementConfig = OPTION_ITEM_CONFIG[leftElementType];

  const onLayout = React.useCallback(
    ({ nativeEvent }: any) => setLayout(nativeEvent.layout),
    [setLayout],
  );

  const onClickLeftElement = useCallback(() => {
    if (selectedElementId === element.id) {
      setSelectedElementId('');

      return;
    }

    const updatedMtfResponse = mtfResponse.filter(
      (response) => response.leftElementId !== element.id,
    );
    onChange(updatedMtfResponse);
    setSelectedElementId(element.id);
    setSelectedElementLayouts &&
      setSelectedElementLayouts((prevValue = {}) => {
        const isElementPresent = !isEmpty(prevValue[element.id]);
        if (!isElementPresent)
          return {
            ...prevValue,
            [element.id]: { ...prevValue[element.id], id: element.id, layout },
          };
        else {
          delete prevValue[element.id].connectedElements;

          return prevValue;
        }
      });
  }, [
    element.id,
    layout,
    mtfResponse,
    onChange,
    selectedElementId,
    setSelectedElementId,
    setSelectedElementLayouts,
  ]);

  useHotkeys(`${index + 1}`, onClickLeftElement, [onClickLeftElement, index]);

  const responsePair = mtfResponse.find((response) => response.leftElementId === element.id);

  const Component = isNative ? BaseButton : Stack;

  return (
    <>
      <Component
        className={cn(
          'flex w-full min-w-0 flex-row overflow-hidden p-0',
          leftElementConfig.classes.listItem,
          {
            'pointer-events-none': isQuestionSubmitted,
          },
          props.containerClassName,
        )}
        data-mtf-id={element.id}
        // @ts-ignore
        role={isQuestionSubmitted ? '' : 'button'}
        style={isNative ? leftElementConfig?.styles?.listItem : undefined}
        onClick={onClickLeftElement}
        onLayout={onLayout}>
        {renderKeyCode({
          className: leftElementConfig.classes.keyCode,
          children: leftElementConfig.renderKeyCodeContent(`${index + 1}`),
          style: isNative ? leftElementConfig.classes.keyCode : undefined,
        })}
        <Box className="min-w-0 flex-1">
          {renderOptionText({
            optionText: element.text,
            className: leftElementConfig.classes.optionText,
            secondaryBackgroundColor: leftElementConfig.classes.secondaryBackgroundColor,
          })}
        </Box>
      </Component>
      {responsePair
        ? renderArrow({
            leftId: responsePair.leftElementId,
            selectedElementLayouts,
            rightId: responsePair.rightElementIds[0],
            containerEl,
            color: '#bfbfbf',
          })
        : null}
    </>
  );
};

export default LeftElementItem;
